// @flow
import {ActionsObservable, ofType} from 'redux-observable';
import {map, switchMap} from 'rxjs/operators';
import {
  ActionCreator,
  buildCatchError,
  createReducer,
} from '../../../store/utils';
import {
  createCollaboratorApi,
  getCollaboratorsApi,
  getCollaboratorApi,
  updateCollaboratorApi,
  endpoints,
} from './api';

import type {Collaborator} from '../../../entities';

import type {Action} from '../../../store/utils';
import type {SaveCollaboratorRequest} from './api';
import {buildRoute} from '../../../http';

export type AccountsConfigAction = Action & {
  error?: string,
  collaboratorRequest?: SaveCollaboratorRequest,
};

export type AccountsConfigState = {
  collaborators?: Collaborator[],
  isFetching?: boolean,
  fetchError?: boolean,
  collaborator?: Collaborator,
  isSaving?: boolean,
  saveSuccess?: boolean,
  saveError?: boolean,
  deleteSuccess?: boolean,
  deleteError?: boolean,
};

export class FetchCollaborators extends ActionCreator<AccountsConfigAction> {}
export class FetchCollaboratorsSuccess extends ActionCreator<AccountsConfigAction> {}
export class FetchCollaboratorsErrorFn extends ActionCreator<AccountsConfigAction> {}

export class FetchCollaborator extends ActionCreator<AccountsConfigAction> {}
export class FetchCollaboratorSuccess extends ActionCreator<AccountsConfigAction> {}
export class FetchCollaboratorErrorFn extends ActionCreator<AccountsConfigAction> {}

export class CollaboratorEmailExists extends ActionCreator<AccountsConfigAction> {}
export class CollaboratorEmailExistsSuccess extends ActionCreator<AccountsConfigAction> {}
export class CollaboratorEmailExistsErrorFn extends ActionCreator<AccountsConfigAction> {}

export class CreateCollaborator extends ActionCreator<AccountsConfigAction> {}
export class CreateCollaboratorSuccess extends ActionCreator<AccountsConfigAction> {}
export class CreateCollaboratorErrorFn extends ActionCreator<AccountsConfigAction> {}

export class UpdateCollaborator extends ActionCreator<AccountsConfigAction> {}
export class UpdateCollaboratorSuccess extends ActionCreator<AccountsConfigAction> {}
export class UpdateCollaboratorErrorFn extends ActionCreator<AccountsConfigAction> {}

export class UpdateCollaboratorCollaborator extends ActionCreator<AccountsConfigAction> {}
export class UpdateCollaboratorCollaboratorSuccess extends ActionCreator<AccountsConfigAction> {}
export class UpdateCollaboratorCollaboratorErrorFn extends ActionCreator<AccountsConfigAction> {}

export class CollaboratorForgotPassword extends ActionCreator<AccountsConfigAction> {}
export class CollaboratorForgotPasswordSuccess extends ActionCreator<AccountsConfigAction> {}
export class CollaboratorForgotPasswordErrorFn extends ActionCreator<AccountsConfigAction> {}

export class UpdateCollaboratorPassword extends ActionCreator<AccountsConfigAction> {}
export class UpdateCollaboratorPasswordSuccess extends ActionCreator<AccountsConfigAction> {}
export class UpdateCollaboratorPasswordErrorFn extends ActionCreator<AccountsConfigAction> {}

export class AccountsResetState extends ActionCreator<AccountsConfigAction> {}

export const actions = {
  fetchCollaborators: (payload: AccountsConfigAction) =>
    new FetchCollaborators(payload),
  fetchCollaborator: (payload: AccountsConfigAction) =>
    new FetchCollaborator(payload),
  createCollaborator: (payload: AccountsConfigAction) =>
    new CreateCollaborator(payload),
  updateCollaborator: (payload: AccountsConfigAction) =>
    new UpdateCollaborator(payload),
  collaboratorForgotPassword: (payload: AccountsConfigAction) =>
    new CollaboratorForgotPassword(payload),
  updateCollaboratorPassword: (payload: AccountsConfigAction) =>
    new UpdateCollaboratorPassword(payload),

  resetState: () => new AccountsResetState(),
};

export const initialState: AccountsConfigState = {};

export default createReducer<AccountsConfigState>(initialState, {
  [FetchCollaborators.type]: state => ({
    ...state,
    collaborators: undefined,
    isFetching: true,
    fetchError: undefined,
  }),
  [FetchCollaboratorsSuccess.type]: (state, action: AccountsConfigAction) => ({
    ...state,
    collaborators: action.data,
    isFetching: false,
    fetchError: undefined,
  }),
  [FetchCollaboratorsErrorFn.type]: (state, action: AccountsConfigAction) => ({
    ...state,
    fetchError: action.error,
    isFetching: false,
  }),
  [FetchCollaborator.type]: state => ({
    ...state,
    collaborator: undefined,
    isFetching: true,
    fetchError: undefined,
  }),
  [FetchCollaboratorSuccess.type]: (state, action: AccountsConfigAction) => ({
    ...state,
    collaborator: action.data,
    isFetching: false,
    fetchError: undefined,
  }),
  [FetchCollaboratorErrorFn.type]: (state, action: AccountsConfigAction) => ({
    ...state,
    fetchError: action.error,
    isFetching: false,
  }),
  [CollaboratorEmailExists.type]: state => ({
    ...state,
    emailExists: undefined,
    // isFetching: true,
    // fetchError: undefined,
  }),
  [CollaboratorEmailExistsSuccess.type]: (
    state,
    action: AccountsConfigAction
  ) => ({
    ...state,
    emailExists: action.data,
    // isFetching: false,
    // fetchError: undefined,
  }),
  [CollaboratorEmailExistsErrorFn.type]: (
    state,
    action: AccountsConfigAction
  ) => ({
    ...state,
    fetchError: action.error,
    // isFetching: false,
  }),

  [CreateCollaborator.type]: state => ({
    ...state,
    isSaving: true,
    saveSuccess: undefined,
    saveError: undefined,
  }),
  [CreateCollaboratorSuccess.type]: state => ({
    ...state,
    isSaving: undefined,
    saveSuccess: true,
  }),
  [CreateCollaboratorErrorFn.type]: (state, action: AccountsConfigAction) => ({
    ...state,
    isSaving: undefined,
    saveSuccess: false,
    saveError: action.error,
  }),
  [UpdateCollaborator.type]: state => ({
    ...state,
    isSaving: true,
    saveSuccess: undefined,
    saveError: undefined,
  }),
  [UpdateCollaboratorSuccess.type]: state => ({
    ...state,
    isSaving: undefined,
    saveSuccess: true,
  }),
  [UpdateCollaboratorErrorFn.type]: (state, action: AccountsConfigAction) => ({
    ...state,
    isSaving: undefined,
    deleteSuccess: false,
    deleteError: action.error,
  }),

  [AccountsResetState.type]: state => ({
    ...initialState,
    collaborators: state.collaborators,
  }),
});

export function getCollaboratorsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(
      FetchCollaborators.type,
      CreateCollaboratorSuccess.type,
      UpdateCollaboratorSuccess.type
    ),
    switchMap(({collaboratorsRequest}: AccountsConfigAction) =>
      getCollaboratorsApi(
        collaboratorsRequest ? collaboratorsRequest : {archived: 'false'}
      ).pipe(
        map((data: Collaborator[]) => new FetchCollaboratorsSuccess({data})),
        buildCatchError(FetchCollaboratorsErrorFn)
      )
    )
  );
}

export function getCollaboratorEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchCollaborator.type),
    switchMap(({collaboratorId}: AccountsConfigAction) =>
      getCollaboratorApi(undefined, {
        route: buildRoute(endpoints.GET_COLLABORATOR, [collaboratorId]),
      }).pipe(
        map((data: Collaborator) => new FetchCollaboratorSuccess({data})),
        buildCatchError(FetchCollaboratorErrorFn)
      )
    )
  );
}

export function createCollaboratorEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(CreateCollaborator.type),
    switchMap(({collaboratorRequest}: AccountsConfigAction) => {
      return createCollaboratorApi(collaboratorRequest).pipe(
        map((data: Collaborator) => new CreateCollaboratorSuccess({data})),
        buildCatchError(CreateCollaboratorErrorFn)
      );
    })
  );
}

export function updateCollaboratorEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(UpdateCollaborator.type),
    switchMap(({id, collaboratorRequest}: AccountsConfigAction) => {
      return updateCollaboratorApi(collaboratorRequest, {
        route: buildRoute(endpoints.UPDATE_COLLABORATOR, [id]),
      }).pipe(
        map((data: Collaborator) => new UpdateCollaboratorSuccess({data})),
        buildCatchError(UpdateCollaboratorErrorFn)
      );
    })
  );
}
